import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DEFAULT_INVITE_MESSAGE =
  "🎁 Ganhe moedas no *FUTURO PDV* comigo!\n\nVocê recebe *+{coins} moedas* de bônus só por criar sua conta, e eu também! Poste, curta, chame corrida ou faça freelas — tudo vira moeda que dá pra sacar.\n\nMeu link: {link}";

/** Admin card: invite message template + signup bonuses (coins & R$) for invitee and referrer, with backfill. */
export default function AdminInviteSettings() {
  const [form, setForm] = useState({ invite_message: "", referred_signup: 50, referral_signup: 50, referred_signup_brl: 0, referral_signup_brl: 0 });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const preview = (form.invite_message || DEFAULT_INVITE_MESSAGE)
    .replaceAll("{coins}", String(form.referred_signup || 0))
    .replaceAll("{link}", "https://seu-link-de-convite")
    .replaceAll("{name}", "Maria");

  const [loaded, setLoaded] = useState(false);
  const dirty = useRef(false);
  const update = (patch) => { dirty.current = true; setForm((f) => ({ ...f, ...patch })); };

  const load = async () => {
    // Status is fetched independently so a slow endpoint never delays the current message.
    axios.get(`${API}/admin/referrals/status`).then((r) => setStatus(r.data)).catch(() => {});
    const [p, c] = await Promise.allSettled([
      axios.get(`${API}/settings/public`, { params: { _t: Date.now() } }),
      axios.get(`${API}/coins/config`),
    ]);
    const pub = p.status === "fulfilled" ? p.value.data : {};
    const cv = c.status === "fulfilled" ? (c.value.data.coin_values || {}) : {};
    // Never clobber text the admin already started typing (slow responses used to reset the field).
    if (!dirty.current) {
      setForm({
        invite_message: pub.invite_message || "",
        referred_signup: cv.referred_signup ?? pub.referred_signup_coins ?? 50,
        referral_signup: cv.referral_signup ?? pub.referral_signup_coins ?? 50,
        referred_signup_brl: pub.referred_signup_brl || 0,
        referral_signup_brl: pub.referral_signup_brl || 0,
      });
    }
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const c = await axios.get(`${API}/coins/config`);
      await axios.put(`${API}/admin/settings`, {
        invite_message: form.invite_message,
        referred_signup_brl: parseFloat(form.referred_signup_brl) || 0,
        referral_signup_brl: parseFloat(form.referral_signup_brl) || 0,
        coin_values: { ...(c.data.coin_values || {}), referred_signup: parseInt(form.referred_signup) || 0, referral_signup: parseInt(form.referral_signup) || 0 },
      });
      // Verify the server really applied the text (guards against an outdated backend silently ignoring the field).
      const check = await axios.get(`${API}/settings/public`, { params: { _t: Date.now() } });
      if ((check.data.invite_message || "") !== form.invite_message.trim()) {
        toast.error("O servidor não aplicou a nova mensagem. Publique a versão mais recente do app e tente de novo.");
        setSaving(false);
        return;
      }
      setSavedAt(new Date());
      dirty.current = false;
      toast.success("Convites e bônus atualizados — valem imediatamente");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setSaving(false);
  };

  const backfill = async () => {
    setFixing(true);
    try {
      const r = await axios.post(`${API}/admin/referrals/backfill`);
      toast.success(`Verificados ${r.data.checked} · convidados corrigidos: ${r.data.fixed_invitees} · indicadores corrigidos: ${r.data.fixed_referrers}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reprocessar"); }
    setFixing(false);
  };

  return (
    <Card className="p-6 space-y-4" data-testid="admin-invite-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center"><Gift className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="text-lg font-bold tracking-tight">Convites & bônus de cadastro</h2>
          <p className="text-xs text-muted-foreground">Mensagem do convite e recompensas de quem convida e de quem entra. Alterações valem na hora.</p>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-sm">Mensagem de convite</Label>
        <Textarea rows={5} value={form.invite_message} placeholder={loaded ? DEFAULT_INVITE_MESSAGE : "Carregando mensagem atual..."} aria-busy={!loaded} onChange={(e) => update({ invite_message: e.target.value })} data-testid="admin-invite-message" />
        <p className="text-[11px] text-muted-foreground">Use <code>{"{link}"}</code> para o link do convite, <code>{"{coins}"}</code> para o bônus em moedas e <code>{"{name}"}</code> para o nome de quem convida. Vazio = mensagem padrão.</p>
        <div className="rounded-lg border border-dashed border-border bg-muted/40 p-3 text-xs whitespace-pre-wrap" data-testid="admin-invite-preview">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Pré-visualização (como o usuário envia)</div>
          {preview}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Moedas para quem entra</Label><Input type="number" min="0" value={form.referred_signup} onChange={(e) => update({ referred_signup: e.target.value })} data-testid="admin-invite-referred-coins" /></div>
        <div className="space-y-1"><Label className="text-xs">Moedas para quem convida</Label><Input type="number" min="0" value={form.referral_signup} onChange={(e) => update({ referral_signup: e.target.value })} data-testid="admin-invite-referrer-coins" /></div>
        <div className="space-y-1"><Label className="text-xs">Crédito R$ para quem entra</Label><Input type="number" min="0" step="0.5" value={form.referred_signup_brl} onChange={(e) => update({ referred_signup_brl: e.target.value })} data-testid="admin-invite-referred-brl" /></div>
        <div className="space-y-1"><Label className="text-xs">Crédito R$ para quem convida</Label><Input type="number" min="0" step="0.5" value={form.referral_signup_brl} onChange={(e) => update({ referral_signup_brl: e.target.value })} data-testid="admin-invite-referrer-brl" /></div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="admin-invite-save">{saving ? "Salvando..." : "Salvar convites e bônus"}</Button>
          {savedAt && <span className="text-[11px] text-emerald-600" data-testid="admin-invite-saved-at">Salvo às {savedAt.toLocaleTimeString("pt-BR")}</span>}
        </div>
        {status && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="admin-invite-status">
            {status.referred_users} cadastros via convite · {status.credited} com bônus · <b className={status.pending ? "text-amber-600" : "text-emerald-600"}>{status.pending} pendentes</b>
            <Button size="sm" variant="outline" className="rounded-full gap-1 h-7" onClick={backfill} disabled={fixing} data-testid="admin-invite-backfill">
              <RefreshCw className={`w-3 h-3 ${fixing ? "animate-spin" : ""}`} /> Reprocessar bônus pendentes
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
